/**
 * The planbook (R1, R2, R4, R5, KTD3): every built plan as rows of Monday-to-Friday weeks, the
 * year as unit blocks, and a month as its weeks. Components only render these. Pure: `today`
 * is a parameter, and dates stay `YYYY-MM-DD` strings.
 */
import { weekDates, weekOf } from './calendar';
import { shortLabels, standardLabel, standardLookup } from './course';
import { effectiveSpans, standardsOn, type UnitSpans } from './days';
import { combinedMonths, dayKind, type YearMonth } from './month';
import { schedule, type ScheduledUnit } from './schedule';
import type { IsoDate, PlanRecord } from './types';

/** A built plan as `/api/week` returns it; the planbook reads only these. */
export type PlanbookPlan = Pick<PlanRecord, 'id' | 'course' | 'plan' | 'standards'>;

/** A built plan with its schedule and its standards' spans, worked out once (the schedule is not cheap). */
export interface ScheduledPlan {
  record: PlanbookPlan;
  scheduled: readonly ScheduledUnit[];
  spans: readonly UnitSpans[];
}

export const schedulePlan = (record: PlanbookPlan): ScheduledPlan =>
  ({ record, scheduled: schedule(record.plan, record.plan.calendar), spans: effectiveSpans(record.plan) });

export interface UnitRef {
  id: string;
  name: string;
}

/** One unit across the year, from its first to its last school day (R4). */
export interface YearBlock {
  unitId: string;
  name: string;
  start: IsoDate;
  /** Never past the calendar's last day; `pastLastDay` says it ran on. */
  end: IsoDate;
  done: boolean;
  pastLastDay: boolean;
  /** The Monday of the week the unit is scheduled to start, unclipped: where choosing the block goes. */
  opens: IsoDate;
}

export interface YearRow {
  planId: string;
  label: string;
  blocks: YearBlock[];
}

export interface YearView {
  months: YearMonth[];
  today: IsoDate;
  rows: YearRow[];
}

/** A planbook day: blank outside the year, greyed and named in a break, and marked where a unit starts (R2). */
export interface PlanbookDay {
  date: IsoDate;
  kind: 'school' | 'break' | 'outside_year';
  breakName?: string;
  /** Set on a unit's first school day. */
  newUnit?: UnitRef;
}

/** One plan in one week (R1): its label, the week's unit, and the codes whose span covers a day of it. */
export interface WeekRow {
  planId: string;
  label: string;
  /** The schedule gives units whole weeks, so a week has at most one per plan. */
  unit: (UnitRef & { index: number }) | null;
  /** In unit then plan order; `code` is the standard's code, or its statement when it has none. */
  codes: { id: string; code: string }[];
  days: PlanbookDay[];
}

const labelsOf = (plans: readonly ScheduledPlan[]) => shortLabels(plans.map(p => p.record.course));

/** R4: the months from the earliest first day to the latest last day, and each plan's units as blocks. */
export function yearView(plans: readonly ScheduledPlan[], today: IsoDate): YearView {
  const labels = labelsOf(plans);
  // Blocks stop at the last day, so the months do too.
  const months = combinedMonths(plans.map(({ record: p }, i) => ({ planId: p.id, colorIndex: i, label: labels[i]!, calendar: p.plan.calendar, rows: [] })));
  const rows = plans.map(({ record: p, scheduled }, i): YearRow => {
    const { lastDay } = p.plan.calendar;
    const blocks = scheduled.flatMap(u => (u.start === null || u.end === null ? [] : [{
      unitId: u.id, name: u.name, start: u.start < lastDay ? u.start : lastDay, end: u.end < lastDay ? u.end : lastDay, done: u.done, pastLastDay: u.pastLastDay,
      opens: weekOf(u.start),
    }]));
    return { planId: p.id, label: labels[i]!, blocks };
  });
  return { months, today, rows };
}

/** R1, R2: each plan's row for the week of `date`, which snaps to its Monday. */
export function weekRows(plans: readonly ScheduledPlan[], date: IsoDate): WeekRow[] {
  const monday = weekOf(date);
  const dates = weekDates(monday);
  const labels = labelsOf(plans);
  return plans.map(({ record: p, scheduled, spans }, i): WeekRow => {
    const { calendar } = p.plan;
    const index = scheduled.findIndex(u => u.weekStarts.includes(monday));
    const u = scheduled[index];
    const unit = u ? { id: u.id, name: u.name, index } : null;
    const textOf = standardLookup(p.standards);
    const codes = [...new Set(dates.flatMap(d => standardsOn(spans, d)))].map(id => ({ id, code: standardLabel(textOf(id)) }));
    const days = dates.map((d): PlanbookDay => {
      const { kind, breakName } = dayKind(calendar, d);
      const day: PlanbookDay = breakName === undefined ? { date: d, kind } : { date: d, kind, breakName };
      return u && kind === 'school' && d === u.start ? { ...day, newUnit: { id: u.id, name: u.name } } : day;
    });
    return { planId: p.id, label: labels[i]!, unit, codes, days };
  });
}
